/**
 * File validation utilities for upload surfaces.
 * Validates file extensions and probes MOV files for unsupported codecs (ProRes, etc.)
 */

import {Input, ALL_FORMATS, BlobSource} from "mediabunny";
import { probeAviVideoStream } from "./aviProbe";

// ============================================================================
// Supported File Types Configuration
// ============================================================================
export const SUPPORTED_AUDIO_EXTENSIONS = ['wav', 'mp3', 'aiff', 'aif', 'm4a', 'flac', 'ogg', 'opus'];
export const SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm'];
export const SUPPORTED_IMAGE_EXTENSIONS = ['bmp', 'jpeg', 'jpg', 'png', 'gif', 'webp'];

export const ALL_SUPPORTED_EXTENSIONS = [
    ...SUPPORTED_AUDIO_EXTENSIONS,
    ...SUPPORTED_VIDEO_EXTENSIONS,
    ...SUPPORTED_IMAGE_EXTENSIONS,
];

// Human-readable format descriptions for the "Supported formats" panel
export const SUPPORTED_FORMATS_INFO = {
    audio: {
        containers: 'WAV, MP3, AIFF, M4A, FLAC, OGG',
        codecs: 'AAC, PCM, OPUS',
    },
    video: {
        containers: 'MP4, M4V, MOV, WEBM',
        codecs: 'H.264, HEVC, VP9, VP8',
        note: 'ProRes and other professional codecs are not supported',
    },
    image: {
        formats: 'BMP, JPEG, PNG, GIF, WEBP',
    },
};

// Known unsupported codec identifiers (from container metadata)
// These are typically ProRes variants and other professional codecs
const UNSUPPORTED_CODEC_IDS = [
    'apch', // ProRes 422 HQ
    'apcn', // ProRes 422
    'apcs', // ProRes 422 LT
    'apco', // ProRes 422 Proxy
    'ap4h', // ProRes 4444
    'ap4x', // ProRes 4444 XQ
    'icod', // Apple Intermediate Codec
    'qtrle', // QuickTime Animation (RLE)
    'rpza', // Apple Video
    'smc',  // Apple Graphics
    'rle',  // Animation codec
];

/**
 * Check if a file extension is supported
 */
export function isExtensionSupported(filename: string): boolean {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ALL_SUPPORTED_EXTENSIONS.includes(ext);
}

/**
 * Get the file extension from a filename (uppercase)
 */
export function getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toUpperCase() || 'Unknown';
}

/**
 * Probe a MOV file to check if its codec is supported for web playback.
 * Uses MediaBunny to detect the actual codec inside the container.
 */
async function probeVideoCodecSupport(file: File): Promise<{
    supported: boolean;
    codec?: string;
    reason?: string;
}> {
    try {
        const source = new BlobSource(file);
        const input = new Input({ source, formats: ALL_FORMATS });
        
        // Get video track
        const videoTrack = await input.getPrimaryVideoTrack();
        if (!videoTrack) {
            // No video track - could be audio-only in video container, allow it
            return { supported: true };
        }
        
        // Check the internal codec ID (raw identifier from container)
        const internalId = videoTrack.internalCodecId;
        const internalIdStr = typeof internalId === 'string' ? internalId.toLowerCase() : '';
        
        // Check against known unsupported codecs
        for (const unsupportedId of UNSUPPORTED_CODEC_IDS) {
            if (internalIdStr === unsupportedId || internalIdStr.startsWith(unsupportedId)) {
                // Map internal ID to human-readable codec name
                let codecName = 'Unknown';
                if (internalIdStr.startsWith('apc') || internalIdStr.startsWith('ap4')) {
                    codecName = 'ProRes';
                } else if (internalIdStr === 'qtrle' || internalIdStr === 'rle') {
                    codecName = 'Animation (RLE)';
                } else if (internalIdStr === 'icod') {
                    codecName = 'Apple Intermediate';
                }
                return {
                    supported: false,
                    codec: codecName,
                    reason: `${codecName} codec is not supported for web playback`,
                };
            }
        }
        
        // Also check if WebCodecs can decode this track
        const canDecode = await videoTrack.canDecode();
        if (!canDecode) {
            // Get codec info for better error message
            const codec = videoTrack.codec || internalIdStr || 'Unknown';
            return {
                supported: false,
                codec: codec.toUpperCase(),
                reason: `${codec.toUpperCase()} codec cannot be decoded in the browser`,
            };
        }
        
        return { supported: true };
    } catch (e) {
        // If probing fails, we can't determine support - allow the file and let later stages handle it
        console.warn('[probeVideoCodecSupport] Failed to probe file:', file.name, e);
        return { supported: true };
    }
}

export interface FileValidationResult {
    mode: 'local_ok' | 'server_process_ok' | 'rejected';
    extension: string;
    codec?: string;
    reason?: string;
}

/**
 * Validate a file for upload support.
 * Checks extension first, then probes MOV files specifically for codec support
 * (since MOV can contain ProRes and other unsupported professional codecs).
 */
export async function validateFileForUpload(file: File): Promise<FileValidationResult> {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const extension = ext.toUpperCase();
    
    // Local-first supported extensions
    if (isExtensionSupported(file.name)) {
        // Only MOV files need codec probing (can contain ProRes, QTRLE, etc.)
        // Other containers (MP4, WebM) typically only have web-compatible codecs
        if (ext === 'mov') {
            const codecResult = await probeVideoCodecSupport(file);
            if (!codecResult.supported) {
                return {
                    mode: 'rejected',
                    extension,
                    codec: codecResult.codec,
                    reason: codecResult.reason,
                };
            }
        }
        return { mode: 'local_ok', extension };
    }

    // Server-processed allowlist (narrow for now):
    // - AVI container with DIVX (MPEG-4 Part 2) video codec.
    if (ext === 'avi') {
        const probe = await probeAviDivxForServerProcessing(file);
        if (probe.eligible) {
            return {
                mode: 'server_process_ok',
                extension,
                codec: probe.codec,
            };
        }
        return {
            mode: 'rejected',
            extension,
            reason: probe.reason || `${extension} file format is not supported`,
        };
    }

    return {
        mode: 'rejected',
        extension,
        reason: `${extension} file format is not supported`,
    };
}

/**
 * Validate multiple files and separate into supported and unsupported.
 * Returns both arrays with detailed information about unsupported files.
 */
export async function validateFilesForUpload(
    files: File[],
    options: { allowServerProcessing?: boolean } = {},
): Promise<{
    localOkFiles: File[];
    serverProcessFiles: Array<{ file: File; display: { name: string; extension: string } }>;
    rejectedFiles: Array<{ name: string; extension: string }>;
}> {
    const allowServerProcessing = options.allowServerProcessing !== false;
    const validationResults = await Promise.all(
        files.map(async (file) => ({
            file,
            validation: await validateFileForUpload(file),
        }))
    );
    
    const localOkFiles: File[] = [];
    const serverProcessFiles: Array<{ file: File; display: { name: string; extension: string } }> = [];
    const rejectedFiles: Array<{ name: string; extension: string }> = [];
    
    for (const { file, validation } of validationResults) {
        // Show codec info if available (e.g., "AVI (DIVX)" or "MOV (ProRes)")
        const displayType = validation.codec
            ? `${validation.extension} (${validation.codec})`
            : validation.extension;

        if (validation.mode === 'local_ok') {
            localOkFiles.push(file);
        } else if (validation.mode === 'server_process_ok' && allowServerProcessing) {
            serverProcessFiles.push({
                file,
                display: { name: file.name, extension: displayType },
            });
        } else {
            rejectedFiles.push({ name: file.name, extension: displayType });
        }
    }
    
    return { localOkFiles, serverProcessFiles, rejectedFiles };
}

async function probeAviDivxForServerProcessing(file: File): Promise<{
    eligible: boolean;
    codec?: string;
    reason?: string;
}> {
    try {
        const normalizeFourCC = (val?: string) => String(val ?? "").replace(/\0/g, "").trim().toUpperCase();

        const probe = await probeAviVideoStream(file);
        if (!probe.isAvi) {
            return { eligible: false, reason: probe.reason || "Not an AVI file" };
        }

        const handler = normalizeFourCC(probe.videoStream?.handler);
        const compression = normalizeFourCC(probe.videoStream?.compression);
        const candidates = [handler, compression].filter((c) => Boolean(c));

        // DIVX is a FourCC for MPEG-4 Part 2 in AVI containers.
        if (candidates.includes("DIVX")) {
            return { eligible: true, codec: "DIVX" };
        }

        if (candidates.length === 0) {
            return { eligible: false, reason: "Could not read AVI video codec" };
        }

        return { eligible: false, reason: "AVI codec is not eligible for server processing" };
    } catch (e) {
        console.warn('[probeAviDivxForServerProcessing] Failed to probe file:', file.name, e);
        return { eligible: false, reason: 'Could not read AVI codec' };
    }
}
